import React, {useMemo} from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import {useFormik} from 'formik';
import {Button, Modal, Form, Input} from 'components/antd';
import {
  PreleadTag,
  useUpdatePreleadTagMutation,
  useDestroyPreleadTagMutation,
  useCurrentClientQuery,
} from 'api';
import {CheckIcon} from '@heroicons/react/20/solid';
import {tagColors} from 'helpers/tagColors';

interface Props {
  preleadTag: PreleadTag;
  onClose: () => void;
}

export default ({preleadTag, onClose}: Props) => {
  const {data: {currentClient: {preleadTags = []} = {}} = {}} = useCurrentClientQuery();

  const [updatePreleadTag] = useUpdatePreleadTagMutation({
    refetchQueries: ['currentClient'],
    onCompleted: () => onClose(),
  });

  const [destroyPreleadTag] = useDestroyPreleadTagMutation({
    refetchQueries: ['currentClient'],
    onCompleted: onClose,
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: preleadTag?.name,
      color: preleadTag?.color,
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('必須項目です'),
    }),
    onSubmit: (values) =>
      updatePreleadTag({
        variables: {
          uuid: preleadTag.uuid,
          attributes: {
            ...values,
            projectGroupId: preleadTag.projectGroup?.id,
          },
        },
      }),
  });

  const alreadyNameExist = useMemo(
    () => preleadTags?.some(
      (tag) => tag.name === formik.values.name &&
               tag.projectGroup?.id === preleadTag?.projectGroup?.id &&
               tag.id !== preleadTag?.id
    ),
    [formik.values.name, preleadTags, preleadTag],
  );

  return (
    <Modal
      destroyOnClose
      visible={Boolean(preleadTag)}
      width={480}
      centered
      transitionName=""
      maskTransitionName=""
      footer={null}
      onCancel={onClose}>
      <ModalContent>
        <h2 className="pb-0">タグを編集</h2>
        <Form layout="vertical" onFinish={formik.handleSubmit}>
          <Form.Item label="タグ名" required>
            <Input
              size="large"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            {alreadyNameExist && (
              <span className="text-red-500 mt-2">
                既に同じ名前のタグが存在します
              </span>
            )}
          </Form.Item>
          <Form.Item label="タグの色を選択">
            {tagColors.map((color, index) => (
              <div
                key={index}
                className="w-[34px] h-[34px] rounded-md cursor-pointer inline-block mr-4 mb-2 relative"
                style={{
                  backgroundColor: color.hex,
                }}
                onClick={() => {
                  formik.setFieldValue('color', color.hex);
                }}>
                {formik.values.color === color.hex && (
                  <CheckIcon className="w-6 h-6 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white" />
                )}
              </div>
            ))}
          </Form.Item>
          <Actions>
            <Button size="large" onClick={onClose}>
              キャンセル
            </Button>
            <div>
              <Button
                danger
                onClick={() => {
                  if (confirm('削除してよろしいですか？')) {
                    destroyPreleadTag({
                      variables: {
                        uuid: preleadTag.uuid,
                      },
                    });
                  }
                }}
                size="large">
                削除
              </Button>
              <Button
                type="primary"
                size="large"
                htmlType="submit"
                style={{marginLeft: '8px'}}
                disabled={!formik.isValid || !formik.dirty || alreadyNameExist}>
                保存
              </Button>
            </div>
          </Actions>
        </Form>
      </ModalContent>
    </Modal>
  );
};

const ModalContent = styled.div`
  h2 {
    font-size: 18px;
    font-weight: 700;
  }

  form {
    margin-top: 1rem;
  }

  label {
    font-weight: bold;
  }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
`;
